import { I18nContext, I18nextProvider, initReactI18next } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import type { i18n } from 'i18next'
import i18next, { createInstance } from 'i18next'
import { SUPPORTED_LANGUAGES } from '../utils/common'
import { contentLocale } from '../utils/localeNav'


// Static imports for all translation files
import enCommon from '../locales/en/common.json'
import frCommon from '../locales/fr/common.json'
import esCommon from '../locales/es/common.json'
import itCommon from '../locales/it/common.json'

import enApp from '../locales/en/app.json'
import frApp from '../locales/fr/app.json'
import esApp from '../locales/es/app.json'
import itApp from '../locales/it/app.json'

const resources = {
    en: {
        common: enCommon,
        app: enApp,
    },
    fr: {
        common: frCommon,
        app: frApp,
    },
    es: {
        common: esCommon,
        app: esApp,
    },
    it: {
        common: itCommon,
        app: itApp,
    },
}

export function CalensyncTranslation({ include_app, children }: { include_app: boolean, children: ReactNode }) {
    const { pathname } = useLocation()
    const currentLng = contentLocale(pathname)
    const i18nRef = useRef<null | i18n>(null)
    const ns = include_app ? ['common', 'app'] : ['common']

    if (!i18nRef.current) {
        const i18nInstance = createInstance()
        i18nInstance.use(initReactI18next)

        // Initialize with empty resources first
        i18nInstance.init({
            lng: currentLng || 'en',
            fallbackLng: 'en',
            ns: ns,
            defaultNS: 'common',
            interpolation: {
                escapeValue: false
            },
            resources
        })
        i18nRef.current = i18nInstance
    }

    return (
        <I18nextProvider i18n={i18nRef.current}>
            {children}
        </I18nextProvider>
    )
}

export function useSyncLng() {
    const { pathname } = useLocation()
    const { i18n } = useContext(I18nContext)
    const navigate = useNavigate()

    useEffect(() => {
        // Get path language
        const pathLng = contentLocale(pathname)
        
        // Get stored language from session storage
        const storedLng = sessionStorage.getItem('lang')

        let targetLng = 'en'

        // Priority 1: Path language if valid
        if (pathLng && SUPPORTED_LANGUAGES.includes(pathLng)) {
            targetLng = pathLng
        }
        // Priority 2: Session storage language if valid 
        else if (storedLng && SUPPORTED_LANGUAGES.includes(storedLng)) {
            targetLng = storedLng
        }

        // Store the selected language
        sessionStorage.setItem('lang', targetLng)

        // Change language if different from current
        if (targetLng !== i18n.language) {
            i18n.changeLanguage(targetLng)
        }

    }, [navigate, pathname, i18n])
}

export function useCalensyncTranslation() {
    const nav = useNavigate()
    const { pathname, search } = useLocation()

    function withLngBase(path: string) {
        if (!path.startsWith('/')) {
            path = `/${path}`
        }
        if (path === '/') {
            path = ''
        }
        return `/${getLng()}${path}`
    }

    function getLng() {
        return contentLocale(pathname) || 'en'
    }

    const { i18n } = useContext(I18nContext)

    return {
        withLngBase,
        getLng,
        i18n,
    }
}